import { createFilterOptions } from "@mui/material";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import { Body, BodyHighlight, Heading2, SmallBody } from "helpers/TypographyComponents";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useNormalizedCompanyUsers } from "hooks/useNormalizedCompanyUsers";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getUserListByCompany } from "store/adminPanelSlice";
import { useAppDispatch, useAppSelector } from "store/store";

import type { TFormCreateAgreement } from "../utils";
import styles from "./style.module.scss";
interface ISignatoriesStep {
    handlePreviousStep: () => void;
}

const SignatoriesStep = ({ handlePreviousStep }: ISignatoriesStep) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { companyInfos } = useCompanySettings();
    const { sendAgreementStatus } = useAppSelector((state) => state.agreements.agFetchStatus);
    const users = useAppSelector((state) => state.adminPanel.users.company.data);
    const userList = useNormalizedCompanyUsers();

    const {
        watch,
        control,
        formState: { errors },
    } = useFormContext<TFormCreateAgreement>();

    useEffect(() => {
        if (users.length) return;
        dispatch(getUserListByCompany());
        // reset();
    }, []);

    const filterOptions = createFilterOptions({
        matchFrom: "any",
        stringify: (option: any) => `${option.firstname} ${option.lastname} ${option.email}`.toLowerCase(),
    });

    const renderSignatoriesOption = (props: any, option: any) => (
        <li {...props} className="social-profile-list" key={`signatory-${option.id}`}>
            <div>
                <span style={{ fontSize: "14px", paddingLeft: option.depth * 12 }}>{`${option.firstname ?? ""} ${
                    option.lastname ?? ""
                }`}</span>
                <SmallBody color="grey" style={{ fontWeight: 300 }}>
                    {option.email}
                </SmallBody>
            </div>
        </li>
    );

    const signatory = watch("signatory");

    return (
        <>
            {sendAgreementStatus === "pending" ? (
                <div className={styles["agreement-validation-modal-spinner"]}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2rem" }}>
                        <Spinner />
                        <BodyHighlight color="black">{t("ContractValidation.ContractPending")}</BodyHighlight>
                    </div>
                </div>
            ) : (
                <div className={styles["agreement-validation-modal-signatories"]}>
                    <div className={styles["agreement-validation-modal-signatories-wrapper"]}>
                        <div className={styles["agreement-validation-modal-signatories-head"]}>
                            <Heading2>{t("ContractValidation.ChoseSignatory")}</Heading2>
                        </div>
                        <div className={styles["agreement-validation-modal-signatories-body"]}>
                            <Body color="grey" style={{ textAlign: "center", width: "36rem" }}>
                                {t("ContractValidation.SignatoriesDescription")}
                            </Body>
                            <div className={styles["agreement-validation-modal-signatories-field"]}>
                                <div className={styles["agreement-validation-modal-signatories-label"]}>
                                    <BodyHighlight>{t("ContractValidation.OneSignatory")}</BodyHighlight>
                                    <Body color="grey">({companyInfos?.name ?? ""})</Body>
                                </div>
                                <AutocompleteFormComponent
                                    sx={{ width: "36rem" }}
                                    options={userList}
                                    required
                                    renderOptionText={(option) =>
                                        `${option?.firstname ?? ""} ${option?.lastname ?? ""}`
                                    }
                                    renderOption={renderSignatoriesOption}
                                    control={control}
                                    controlName="signatory"
                                    placeholder={t("ContractValidation.ChoseSignatory")}
                                    filterOptions={filterOptions}
                                    error={errors.signatory}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles["agreement-validation-modal-actions"]}>
                        <StyledButton buttonType="secondary" onClick={handlePreviousStep}>
                            {t("ContractValidation.ReturnToTemplates")}
                        </StyledButton>
                        <StyledButton buttonType="primary" type="submit" disabled={!signatory?.id}>
                            {t("ContractValidation.GenerateContract")}
                        </StyledButton>
                    </div>
                </div>
            )}
        </>
    );
};

export default SignatoriesStep;
